import { ChangeDetectionStrategy, Component, DestroyRef, inject, ViewChild } from '@angular/core';
import { API, ClientDTO, RoleDTO, UserClientFullDTO } from 'src/app/services/api/api';
import { ErrorService } from 'src/app/services/error.service';
import { ModalService } from 'src/app/services/gui/modal/modal.service';
import { UserService } from 'src/app/services/user.service';
import { UtilService } from 'src/app/services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequestClass } from '../../../services/global/RequestClass';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig, FilterPanelComponent } from '@ngx-seatback/layout';

@Component({
  selector: 'sb-page-admin-membership',
  templateUrl: './admin-membership.component.html',
  styleUrls: ['./admin-membership.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminMembershipComponent extends RequestClass {
  public readonly userStatus = inject(UserService);
  public readonly modal = inject(ModalService);
  public readonly errorHandler = inject(ErrorService);
  public readonly util = inject(UtilService);
  public readonly translate = inject(TranslateService);
  protected readonly api = inject(API);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stringTransformation = inject(StringTransformationService);

  @ViewChild('FilterPanelRef') filterPanelRef: FilterPanelComponent;

  // table wrapper variables
  users: UserClientFullDTO[] = [];

  // filter variables
  blocked: boolean | null = null;
  filteredRoles: RoleDTO[] = null;
  filteredClients: ClientDTO[] = null;
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    button: { visibleWithoutChanges: true },
  };

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const clientIds = this.filteredClients?.length > 0 ? this.filteredClients.map(client => client.id) : null;
    const roleIds = this.filteredRoles?.length > 0 ? this.filteredRoles.map(role => role.id) : null;
    const sub = this.api
      ._admin_Membership_retrieveAll(
        {
          user_search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          blocked: this.blocked,
          client_id: clientIds,
          hasRole_id: roleIds,
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (users: UserClientFullDTO[]) => {
          this.users = users;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
    this.blocked = filterMap.get('blocked')?.[0];
    this.filteredRoles = filterMap.get('roles');
    this.filteredClients = filterMap.get('clients');
  }
}
